import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

export default function MatchPairs({ 
  pairs = [],
  instruction,
  onAnswer,
  disabled = false 
}) {
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [wrongPair, setWrongPair] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [mistakes, setMistakes] = useState(0);
  const [arrowPaths, setArrowPaths] = useState([]); // [{path, color}]
  const { playCorrect, playIncorrect, playClick, playLessonComplete } = useSounds();
  
  const containerRef = useRef(null);
  const leftRefs = useRef({});
  const rightRefs = useRef({});

  const safePairs = Array.isArray(pairs) ? pairs : [];

  // Initialize shuffled items when pairs change
  const shuffledItems = React.useMemo(() => {
    const safePairsInMemo = Array.isArray(pairs) ? pairs : [];
    if (safePairsInMemo.length === 0) {
      return { left: [], right: [] };
    }
    const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);
    return {
      left: shuffleArray(safePairsInMemo.map((p, i) => ({ ...p, id: i, side: 'left' }))),
      right: shuffleArray(safePairsInMemo.map((p, i) => ({ ...p, id: i, side: 'right' })))
    };
  }, [pairs]);

  useEffect(() => {
    setLeftItems(shuffledItems.left);
    setRightItems(shuffledItems.right);
  }, [shuffledItems]);

  // Calculate arrow path between two elements
  const calculateArrowPath = useCallback((leftId, rightId) => {
    const leftEl = leftRefs.current[leftId];
    const rightEl = rightRefs.current[rightId];
    const container = containerRef.current;
    
    if (!leftEl || !rightEl || !container) return null;
    
    const containerRect = container.getBoundingClientRect();
    const leftRect = leftEl.getBoundingClientRect();
    const rightRect = rightEl.getBoundingClientRect();
    
    const startX = leftRect.right - containerRect.left;
    const startY = leftRect.top + leftRect.height / 2 - containerRect.top;
    const endX = rightRect.left - containerRect.left - 8; // Small gap for arrow
    const endY = rightRect.top + rightRect.height / 2 - containerRect.top;
    
    // Bezier curve control points
    const midX = (startX + endX) / 2;
    
    return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
  }, []);

  // Update arrow when a match is made
  const addArrow = useCallback((leftId, rightId) => {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      const path = calculateArrowPath(leftId, rightId);
      if (path) {
        setArrowPaths(prev => [...prev, { path, leftId, rightId }]);
      }
    });
  }, [calculateArrowPath]);

  const handleLeftClick = (item) => {
    if (matchedPairs.includes(item.id) || disabled || isCompleted) return;
    try { playClick?.(); } catch(e) { /* ignore */ }
    setSelectedLeft(item);
    setWrongPair(null);
    
    if (selectedRight) {
      checkMatch(item, selectedRight);
    }
  };

  const handleRightClick = (item) => {
    if (matchedPairs.includes(item.id) || disabled || isCompleted) return;
    try { playClick?.(); } catch(e) { /* ignore */ }
    setSelectedRight(item);
    setWrongPair(null);
    
    if (selectedLeft) {
      checkMatch(selectedLeft, item);
    }
  };

  const checkMatch = (left, right) => {
    if (left.id === right.id) {
      // Correct match - add arrow
      try { playCorrect?.(); } catch(e) { /* ignore */ }
      setMatchedPairs(prev => [...prev, left.id]);
      addArrow(left.id, right.id);
      setSelectedLeft(null);
      setSelectedRight(null);
      
      // Check if all matched
      if (matchedPairs.length + 1 === safePairs.length) {
        setIsCompleted(true);
        try { playLessonComplete?.(); } catch(e) { /* ignore */ }
        setTimeout(() => {
          onAnswer?.(mistakes === 0, mistakes);
        }, 1000);
      }
    } else {
      // Wrong match
      try { playIncorrect?.(); } catch(e) { /* ignore */ }
      setWrongPair({ left: left.id, right: right.id });
      setMistakes(prev => prev + 1);
      
      setTimeout(() => {
        setSelectedLeft(null);
        setSelectedRight(null);
        setWrongPair(null);
      }, 800);
    }
  };

  const getLeftStyle = (item) => {
    if (matchedPairs.includes(item.id)) return 'bg-green-100 border-green-500 text-green-700';
    if (wrongPair?.left === item.id) return 'bg-red-100 border-red-500 text-red-700 animate-shake';
    if (selectedLeft?.id === item.id) return 'bg-blue-100 border-blue-500 text-blue-700 scale-105 shadow-lg';
    return 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md text-gray-700';
  };

  const getRightStyle = (item) => {
    if (matchedPairs.includes(item.id)) return 'bg-green-100 border-green-500 text-green-700';
    if (wrongPair?.right === item.id) return 'bg-red-100 border-red-500 text-red-700 animate-shake';
    if (selectedRight?.id === item.id) return 'bg-blue-100 border-blue-500 text-blue-700 scale-105 shadow-lg';
    return 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md text-gray-700';
  };

  // If no pairs, show error message
  if (safePairs.length === 0) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center py-8">
        <p className="text-gray-500">Eşleştirme verisi yüklenemedi.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Instruction */}
      <div className="mb-4">
        <p className="text-gray-500 text-sm">{instruction || 'Eşleşen çiftleri bulun'}</p>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          <span className="text-green-600 font-medium">{matchedPairs.length}/{safePairs.length}</span>
        </div>
        {mistakes > 0 && (
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-600 font-medium">{mistakes} hata</span>
          </div>
        )}
      </div>

      {/* Matching Area with SVG overlay */}
      <div ref={containerRef} className="relative">
        {/* SVG for arrows */}
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ overflow: 'visible', zIndex: 5 }}
        >
          <defs>
            <marker
              id="arrowhead-green"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e" />
            </marker>
          </defs>
          
          {/* Draw completed connections */}
          {arrowPaths.map((arrow, idx) => (
            <path
              key={`arrow-${idx}`}
              d={arrow.path}
              stroke="#22c55e"
              strokeWidth="3"
              fill="none"
              markerEnd="url(#arrowhead-green)"
              className="transition-all duration-500"
              style={{
                strokeDasharray: 1000,
                strokeDashoffset: 0,
                animation: 'drawArrow 0.5s ease-out forwards'
              }}
            />
          ))}
        </svg>

        <div className="grid grid-cols-2 gap-12">
          {/* Left Column */}
          <div className="space-y-3">
            {leftItems.map((item) => (
              <button
                key={`left-${item.id}`}
                ref={el => { leftRefs.current[item.id] = el; }}
                onClick={() => handleLeftClick(item)}
                onTouchEnd={(e) => { e.preventDefault(); handleLeftClick(item); }}
                disabled={matchedPairs.includes(item.id) || disabled || isCompleted}
                className={cn(
                  'w-full p-4 rounded-xl border-2 font-bold text-lg text-center transition-all duration-200 cursor-pointer select-none touch-manipulation relative z-10',
                  getLeftStyle(item)
                )}
              >
                {item.left}
                {matchedPairs.includes(item.id) && (
                  <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
              </button>
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            {rightItems.map((item) => (
              <button
                key={`right-${item.id}`}
                ref={el => { rightRefs.current[item.id] = el; }}
                onClick={() => handleRightClick(item)}
                onTouchEnd={(e) => { e.preventDefault(); handleRightClick(item); }}
                disabled={matchedPairs.includes(item.id) || disabled || isCompleted}
                className={cn(
                  'w-full p-4 rounded-xl border-2 font-bold text-lg text-center transition-all duration-200 cursor-pointer select-none touch-manipulation relative z-10',
                  getRightStyle(item)
                )}
              >
                {item.right}
                {matchedPairs.includes(item.id) && (
                  <CheckCircle2 className="absolute left-2 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Help Text */}
      {!isCompleted && matchedPairs.length === 0 && (
        <div className="mt-4 text-center text-sm text-gray-400">
          💡 Sol taraftan bir öğe seçin, sonra sağ taraftan eşleşen öğeyi seçin
        </div>
      )}

      {/* Completion Message */}
      {isCompleted && (
        <div className={cn(
          'mt-6 p-4 rounded-xl',
          mistakes === 0 ? 'bg-green-100 border border-green-200' : 'bg-amber-100 border border-amber-200'
        )}>
          <div className="flex items-center gap-2">
            {mistakes === 0 ? (
              <>
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-700">Mükemmel! Hiç hata yapmadın!</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-6 h-6 text-amber-600" />
                <span className="font-bold text-amber-700">Tamamlandı! {mistakes} hata ile</span>
              </>
            )}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
        @keyframes drawArrow {
          from {
            stroke-dashoffset: 1000;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
      `}} />
    </div>
  );
}
